// src/common/bookingUtils.ts

/**
 * Updates each booking object's totalAmount to be the sum of its bookingItems' amount.
 * Mutates the array in-place and returns it for chaining.
 */
export function updateTotalAmountForBookings(bookings: Array<{ bookingItems: { amount: number }[], totalAmount?: number }>): typeof bookings {
  return bookings.map(booking => ({
    ...booking,
    totalAmount: Array.isArray(booking.bookingItems)
      ? booking.bookingItems.reduce((sum, item) => sum + (item.amount || 0), 0)
      : 0
  }));
}

/**
 * Migration Note (MakeupArtistId -> ArtistId):
 * The backend view vActiveArtistDateSlot now exposes ArtistId (PascalCase) instead of MakeupArtistId.
 * Existing legacy payloads might still send makeupArtistId (camelCase) during transition.
 * Use this interface + mapper to normalize to artistId everywhere in UI without touching
 * booking/service price code that intentionally still relies on makeupArtistId in other endpoints.
 */
export interface ActiveArtistDateSlotRow {
  artistId: number;
  bookingId: number;
  eventDate: string; // ISO 8601 string
  bookingSlotId: number;
  bookingSlotName: string;
}

export function mapActiveArtistDateSlot(raw: any): ActiveArtistDateSlotRow {
  return {
    artistId: raw?.ArtistId ?? raw?.artistId ?? raw?.MakeupArtistId ?? raw?.makeupArtistId ?? 0,
    bookingId: raw?.BookingId ?? raw?.bookingId ?? raw?.booking_id ?? 0,
    eventDate: raw?.EventDate ?? raw?.eventDate ?? raw?.event_date ?? '',
    bookingSlotId: raw?.BookingSlotId ?? raw?.bookingSlotId ?? raw?.booking_slot_id ?? 0,
    bookingSlotName: raw?.BookingSlotName ?? raw?.bookingSlotName ?? raw?.booking_slot_name ?? ''
  };
}

export function mapActiveArtistDateSlots(rows: any[]): ActiveArtistDateSlotRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapActiveArtistDateSlot);
}
